import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import axios from 'axios';
import { Loader2, ArrowLeft, QrCode, CheckCircle2 } from 'lucide-react';
import { Toaster, toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const NewTransaction = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [createdTransaction, setCreatedTransaction] = useState(null);
  const [formData, setFormData] = useState({
    amount: '',
    currency: 'LRD',
    description: '',
    reference: '',
    network: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.network) {
      toast.error('Please select a payment network');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/transactions`, {
        amount: parseFloat(formData.amount),
        currency: formData.currency,
        description: formData.description,
        reference: formData.reference,
        network: formData.network
      }, { withCredentials: true });
      
      setCreatedTransaction(response.data);
      toast.success('Transaction created!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create transaction');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateAnother = () => {
    setCreatedTransaction(null);
    setFormData({
      amount: '',
      currency: 'LRD',
      description: '',
      reference: '',
      network: ''
    });
  };

  if (createdTransaction) {
    return (
      <div data-testid="transaction-created">
        <Toaster richColors position="top-right" />
        
        <Button
          variant="ghost"
          className="mb-6"
          onClick={() => navigate('/app/transactions')}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Transactions
        </Button>

        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 mx-auto mb-6 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            
            <h2 className="text-2xl font-semibold text-[#0A1628] mb-2">
              Transaction Created
            </h2>
            <p className="text-slate-500 mb-6">
              Share the QR code with your customer to complete payment
            </p>

            <div className="bg-slate-50 rounded-xl p-6 mb-6">
              <div className="font-mono text-lg text-[#0077B6] mb-4">
                {createdTransaction.transaction_id}
              </div>
              
              {createdTransaction.qr_code && (
                <div className="flex justify-center mb-4">
                  <img 
                    src={`data:image/png;base64,${createdTransaction.qr_code}`}
                    alt="Transaction QR Code"
                    className="w-48 h-48 rounded-lg border border-slate-200"
                    data-testid="transaction-qr-code"
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="text-left">
                  <span className="text-slate-500">Amount</span>
                  <div className="font-semibold text-slate-700">
                    {createdTransaction.currency || 'LRD'} {new Intl.NumberFormat().format(createdTransaction.amount)}
                  </div>
                </div>
                <div className="text-left">
                  <span className="text-slate-500">Network</span>
                  <div className="font-semibold text-slate-700">{createdTransaction.network}</div>
                </div>
                <div className="text-left">
                  <span className="text-slate-500">Reference</span>
                  <div className="font-semibold text-slate-700">{createdTransaction.reference}</div>
                </div>
                <div className="text-left">
                  <span className="text-slate-500">Status</span>
                  <div className="font-semibold text-amber-600 capitalize">{createdTransaction.status}</div>
                </div>
              </div>
            </div>

            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={handleCreateAnother}>
                Create Another
              </Button>
              <Button 
                className="btn-accent"
                onClick={() => navigate(`/app/transactions/${createdTransaction.transaction_id}`)}
              >
                View Details
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="new-transaction-page" className="animate-fadeIn">
      <Toaster richColors position="top-right" />
      
      {/* Animation Styles */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out forwards; }
        .hover-lift { transition: all 0.3s ease; }
        .hover-lift:hover { transform: translateY(-2px); box-shadow: 0 12px 24px -8px rgba(0,0,0,0.12); }
      `}</style>
      
      <Button
        variant="ghost"
        className="mb-6 transition-all duration-200 hover:translate-x-[-4px] group"
        onClick={() => navigate('/app/dashboard')}
      >
        <ArrowLeft className="w-4 h-4 mr-2 transition-transform group-hover:-translate-x-1" />
        Back to Dashboard
      </Button>

      <div className="max-w-xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-[#0A1628]">Create Transaction</h1>
          <p className="text-slate-500 mt-1">
            Enter payment details to generate a new transaction
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 hover-lift">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="amount">Amount *</Label>
              <div className="flex gap-3">
                <Select 
                  value={formData.currency} 
                  onValueChange={(value) => setFormData({ ...formData, currency: value })}
                >
                  <SelectTrigger className="w-24 h-12 transition-all duration-200 hover:border-teal-500" data-testid="currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="LRD">LRD</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="h-12 text-lg flex-1 transition-all duration-200 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                  placeholder="0.00"
                  required
                  data-testid="amount-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="What is this payment for?"
                required
                data-testid="description-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="reference">Reference *</Label>
              <Input
                id="reference"
                type="text"
                value={formData.reference}
                onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                className="h-12"
                placeholder="INV-001, ORD-123, etc."
                required
                data-testid="reference-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="network">Payment Network *</Label>
              <Select 
                value={formData.network} 
                onValueChange={(value) => setFormData({ ...formData, network: value })}
              >
                <SelectTrigger className="h-12" data-testid="network-select">
                  <SelectValue placeholder="Select network" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="MTN">MTN Mobile Money</SelectItem>
                  <SelectItem value="Orange">Orange Money</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button
              type="submit"
              className="w-full h-12 btn-accent"
              disabled={isLoading}
              data-testid="create-transaction-btn"
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <QrCode className="w-5 h-5 mr-2" />
                  Create Transaction & Generate QR
                </>
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default NewTransaction;
